/**
 * AdminDashboardPage - Overview with key metrics and quick actions
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  getAdminDashboard,
  type AdminDashboardStats,
  type DashboardRequestRow,
} from '../../api/admin';
import { useAuth } from '../../context/AuthContext';
import { REQUEST_STATUS_LABELS } from '../../utils/requestStatus';

const REVENUE_VISIBLE_EMAILS = [
  'rsaab@stagedooraccess.com',
  'flavio+admin@mapmatix.com',
];

/**
 * Rows shown per SDA-263 request panel before "Show all" is clicked. Group Tickets
 * at full height otherwise dominates the page and pushes everything below it down.
 */
const REQUEST_COLLAPSED_ROWS = 5;

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};

/**
 * Formats a date value for display using the short month + day + year format.
 * For YYYY-MM-DD date-only strings, appends a local-time midnight suffix to
 * prevent the UTC parsing from shifting the date to the prior day in
 * US/Eastern and similar negative-offset timezones.
 */
function formatDisplayDate(value: string): string {
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? value + 'T00:00:00'
    : value;
  const d = new Date(normalized);
  return isNaN(d.getTime())
    ? value
    : d.toLocaleDateString('en-US', DATE_FORMAT_OPTIONS);
}

/**
 * Age of a request in whole days, for the aging pill on the SDA-263 fulfillment
 * queues. Date-only strings get the same local-midnight normalization as
 * formatDisplayDate so the count doesn't skew by a day in US/Eastern.
 * Future/unparseable timestamps clamp to "Today" rather than showing a negative.
 */
function formatAge(value: string): string {
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? value + 'T00:00:00'
    : value;
  const then = new Date(normalized).getTime();
  if (isNaN(then)) return '';
  const days = Math.floor((Date.now() - then) / 86400000);
  if (days <= 0) return 'Today';
  return days === 1 ? '1 day' : `${days} days`;
}

/**
 * Formats cents as whole dollars (no cents) so MTD stat values fit inside
 * the 6-column card grid at xl widths (SDA-251). Exact amounts remain
 * available on the Bookings/Payouts pages via the shared formatCurrency.
 */
function formatCurrencyWhole(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

export default function AdminDashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState<AdminDashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const showRevenue = REVENUE_VISIBLE_EMAILS.includes(user?.email ?? '');

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getAdminDashboard();
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div>
        <div className="h-10 bg-warm-200 rounded w-1/4 animate-pulse mb-8" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5 sm:mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm p-3 sm:p-6 animate-pulse">
              <div className="h-4 bg-warm-100 rounded w-1/2 mb-4" />
              <div className="h-8 bg-warm-100 rounded w-2/3" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 text-center">
        <p className="text-danger-600 mb-4">{error}</p>
        <button onClick={loadDashboard} className="btn-primary">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-5 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900">
          Admin Dashboard
        </h1>
        <p className="text-warm-600 mt-1 hidden sm:block">
          Welcome back. Here's what's happening today.
        </p>
      </div>

      {/* Stats Grid — 6 cards for revenue-visible admins (SDA-233, SDA-246), 4 for everyone else */}
      <div className={`grid grid-cols-2 ${showRevenue ? 'lg:grid-cols-3 xl:grid-cols-6' : 'lg:grid-cols-4'} gap-3 sm:gap-4 mb-5 sm:mb-8`}>
        <StatCard
          label="Pending Inquiries"
          value={stats?.pending_inquiries || 0}
          icon={<InquiryIcon />}
          color="warning"
          link="/admin/inquiries"
          highlight={stats?.pending_inquiries ? stats.pending_inquiries > 0 : false}
        />
        <StatCard
          label="Upcoming Classes"
          value={stats?.upcoming_classes || 0}
          icon={<CalendarIcon />}
          color="success"
          link="/admin/bookings"
        />
        <StatCard
          label="Active Shows"
          value={stats?.active_shows || 0}
          icon={<ShowIcon />}
          color="primary"
          link="/admin/shows"
        />
        {showRevenue && (
          <>
            <StatCard
              label="Classes Sold (MTD)"
              value={stats?.classes_sold_mtd || 0}
              icon={<CalendarIcon />}
              color="primary"
              link="/admin/bookings"
            />
            <StatCard
              label="Revenue (MTD)"
              value={formatCurrencyWhole(stats?.revenue_mtd_cents || 0)}
              icon={<RevenueIcon />}
              color="gold"
            />
            <StatCard
              label="Cash (MTD)"
              value={formatCurrencyWhole(stats?.cash_mtd_cents || 0)}
              icon={<RevenueIcon />}
              color="success"
            />
          </>
        )}
      </div>

      {/* Quick Actions + Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
          <h2 className="text-lg font-display font-bold text-warm-900 mb-4">
            Quick Actions
          </h2>
          <div className="space-y-3">
            <QuickAction
              label="Review Inquiries"
              description={`${stats?.pending_inquiries || 0} pending`}
              href="/admin/inquiries"
              icon={<InboxIcon />}
              highlight={stats?.pending_inquiries ? stats.pending_inquiries > 0 : false}
            />
            <QuickAction
              label="Send Campaign"
              description="Email studios about shows"
              href="/admin/campaigns/new"
              icon={<SendIcon />}
            />
            <QuickAction
              label="Create Tour Stop"
              description="Add a new tour date"
              href="/admin/tour-stops/new"
              icon={<MapPinIcon />}
            />
            <QuickAction
              label="Add New Show"
              description="Create a new Broadway show"
              href="/admin/shows/new"
              icon={<PlusIcon />}
            />
            <QuickAction
              label="Add Faculty"
              description="Invite a new faculty member"
              href="/admin/faculty/new"
              icon={<UserPlusIcon />}
            />
            <QuickAction
              label="View Bookings"
              description="Manage all bookings"
              href="/admin/bookings"
              icon={<BookingsIcon />}
            />
            <QuickAction
              label="View Waitlist"
              description={`${stats?.waitlist_signups || 0} this week`}
              href="/admin/waitlist"
              icon={<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
              highlight={(stats?.waitlist_signups ?? 0) > 0}
            />
          </div>
        </div>

        {/* Recent Bookings */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-display font-bold text-warm-900">
              Recent Bookings
            </h2>
            <Link
              to="/admin/bookings"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              View all →
            </Link>
          </div>
          {stats?.recent_bookings && stats.recent_bookings.length > 0 ? (
            <div className="space-y-1">
              {stats.recent_bookings.slice(0, 5).map((booking) => (
                <Link
                  key={booking.id}
                  to={`/admin/bookings/${booking.id}`}
                  className="block px-3 py-2.5 rounded-lg hover:bg-warm-50 transition-colors"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-warm-900 truncate">
                          {booking.studio_name}
                        </p>
                        {booking.class_count > 0 && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-warm-100 text-warm-600 flex-shrink-0">
                            {booking.class_count} {booking.class_count === 1 ? 'class' : 'classes'}
                          </span>
                        )}
                        {booking.needs_faculty && booking.status !== 'cancelled' && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-warning-100 text-warning-700 flex-shrink-0">
                            <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            Unassigned
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-warm-500 mt-0.5">
                        {booking.show_name}
                        {booking.class_date && ` · ${formatDisplayDate(booking.class_date)}`}
                      </p>
                      <p className="text-[11px] text-warm-400 mt-0.5">
                        Booked {formatDisplayDate(booking.created_at)}
                      </p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${getBookingStatusStyle(booking.status)}`}>
                      {booking.status}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-warm-500 text-sm text-center py-4">
              No recent bookings
            </p>
          )}
        </div>

        {/* Response Needed */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-display font-bold text-warm-900">
              Response Needed
            </h2>
            <Link
              to="/admin/inquiries?sla=breached"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              View all →
            </Link>
          </div>
          {stats?.sla_breached_count && stats.sla_breached_count > 0 ? (
            <div className="space-y-3">
              <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-danger-100 flex items-center justify-center">
                    <span className="text-danger-600 font-bold">
                      {stats.sla_breached_count}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-danger-800">
                      Overdue (24h+)
                    </p>
                    <p className="text-xs text-danger-600">
                      Inquiries waiting over 24 hours
                    </p>
                  </div>
                </div>
              </div>
              {stats.sla_warning_count > 0 && (
                <div className="p-4 bg-warning-50 border border-warning-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-warning-100 flex items-center justify-center">
                      <span className="text-warning-600 font-bold">
                        {stats.sla_warning_count}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-warning-800">
                        Due Soon (12-24h)
                      </p>
                      <p className="text-xs text-warning-600">
                        Inquiries 12-24 hours
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-success-100 flex items-center justify-center">
                <svg className="w-6 h-6 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm text-warm-500">
                All inquiries on track
              </p>
            </div>
          )}
        </div>

      </div>

      {/* Fulfillment queues (SDA-263) — marketing materials + group tickets.
          Kept out of the stat-card grid on purpose: that grid is already at 6
          cards for revenue-visible admins and SDA-251 was an overflow fix. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mt-4 sm:mt-6">
        <RequestPanel
          title="Marketing Materials"
          subtitle="Open requests"
          rows={stats?.open_marketing_requests ?? []}
          count={stats?.open_marketing_count ?? 0}
          emptyMessage="No marketing materials outstanding"
        />
        <RequestPanel
          title="Group Tickets"
          subtitle="Open requests"
          rows={stats?.open_group_ticket_requests ?? []}
          count={stats?.open_group_ticket_count ?? 0}
          emptyMessage="No group ticket requests"
        />
      </div>
    </div>
  );
}

// Stat Card Component
interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color: 'primary' | 'success' | 'warning' | 'gold';
  link?: string;
  highlight?: boolean;
}

function StatCard({ label, value, icon, color, link, highlight }: StatCardProps) {
  const colorStyles = {
    primary: 'bg-primary-50 text-primary-600',
    success: 'bg-success-50 text-success-600',
    warning: 'bg-warning-50 text-warning-600',
    gold: 'bg-primary-50 text-primary-600',
  };

  const content = (
    <div className={`bg-white rounded-xl shadow-sm p-3 sm:p-6 ${highlight ? 'ring-2 ring-warning-400' : ''}`}>
      <div className="flex items-center justify-between mb-2 sm:mb-3">
        <span className="text-xs sm:text-sm font-medium text-warm-500">{label}</span>
        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center ${colorStyles[color]}`}>
          {icon}
        </div>
      </div>
      <p className="text-xl sm:text-3xl font-bold text-warm-900">{value}</p>
    </div>
  );

  if (link) {
    return (
      <Link to={link} className="block hover:shadow-md transition-shadow rounded-xl">
        {content}
      </Link>
    );
  }

  return content;
}

// Request Panel Component (SDA-263)
// A read-only, oldest-first fulfillment queue: marketing materials or group
// tickets. Rows link straight to the booking, where both requests are actioned
// (including their status — the badge here is display-only; labels come from
// the shared utils/requestStatus module the booking page also uses).

const REQUEST_STATUS_STYLES: Record<string, string> = {
  waiting_studio_material: 'bg-warning-100 text-warning-700',
  new: 'bg-warning-100 text-warning-700',
  in_progress: 'bg-primary-100 text-primary-700',
  waiting_ticket_order: 'bg-primary-100 text-primary-700',
  complete: 'bg-success-100 text-success-700',
};

interface RequestPanelProps {
  title: string;
  subtitle: string;
  rows: DashboardRequestRow[];
  /** Exact total from the API; `rows` is capped at 10 by the worker. */
  count: number;
  emptyMessage: string;
}

function RequestPanel({ title, subtitle, rows, count, emptyMessage }: RequestPanelProps) {
  const [showAll, setShowAll] = useState(false);
  const visibleRows = showAll ? rows : rows.slice(0, REQUEST_COLLAPSED_ROWS);
  const hiddenCount = count - visibleRows.length;

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-display font-bold text-warm-900">{title}</h2>
          <p className="text-xs text-warm-500 mt-0.5">{subtitle}</p>
        </div>
        {count > 0 && (
          <span className="inline-flex items-center justify-center min-w-[1.75rem] px-2 py-0.5 rounded-full text-sm font-bold bg-warning-100 text-warning-700 flex-shrink-0">
            {count}
          </span>
        )}
      </div>
      {rows.length > 0 ? (
        <>
          <div className="space-y-1">
            {visibleRows.map((row) => (
              <Link
                key={row.id}
                to={`/admin/bookings/${row.id}`}
                className="block px-3 py-2.5 rounded-lg hover:bg-warm-50 transition-colors"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-warm-900 truncate">
                      {row.studio_name}
                    </p>
                    <p className="text-xs text-warm-500 mt-0.5 truncate">
                      {row.show_name}
                      {row.booking_number && ` · ${row.booking_number}`}
                    </p>
                    <p className="text-[11px] text-warm-400 mt-0.5">
                      {row.class_date
                        ? `Class ${formatDisplayDate(row.class_date)}`
                        : 'No class scheduled'}
                      {' · '}
                      Booked {formatDisplayDate(row.created_at)}
                    </p>
                    {row.last_reminder_at && (
                      <p className="text-[11px] text-warm-400 mt-0.5">
                        Reminder sent {formatDisplayDate(row.last_reminder_at)}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-warm-100 text-warm-600">
                      {formatAge(row.created_at)}
                    </span>
                    {row.request_status && REQUEST_STATUS_LABELS[row.request_status] && (
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          REQUEST_STATUS_STYLES[row.request_status] ?? 'bg-warm-100 text-warm-600'
                        }`}
                      >
                        {REQUEST_STATUS_LABELS[row.request_status]}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
          {/* Collapsed to 5 rows so neither panel dominates the page. "Show all"
              reveals everything the payload carries; anything beyond that is
              stated rather than hidden, since count is the exact DB total. */}
          {rows.length > REQUEST_COLLAPSED_ROWS && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium mt-3 px-3"
            >
              {showAll
                ? 'Show less'
                : `Show all ${Math.min(rows.length, count)}`}
            </button>
          )}
          {hiddenCount > 0 && (
            <p className="text-xs text-warm-500 mt-3 px-3">
              Showing {visibleRows.length} of {count}
            </p>
          )}
        </>
      ) : (
        <div className="text-center py-4">
          <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-success-100 flex items-center justify-center">
            <svg className="w-6 h-6 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-sm text-warm-500">{emptyMessage}</p>
        </div>
      )}
    </div>
  );
}

// Quick Action Component
interface QuickActionProps {
  label: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  highlight?: boolean;
}

function QuickAction({ label, description, href, icon, highlight }: QuickActionProps) {
  return (
    <Link
      to={href}
      className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
        highlight
          ? 'bg-warning-50 hover:bg-warning-100 border border-warning-200'
          : 'hover:bg-warm-50'
      }`}
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
        highlight ? 'bg-warning-100 text-warning-600' : 'bg-warm-100 text-warm-600'
      }`}>
        {icon}
      </div>
      <div>
        <p className="text-sm font-medium text-warm-900">{label}</p>
        <p className="text-xs text-warm-500">{description}</p>
      </div>
    </Link>
  );
}

// Helper function for booking status styles
function getBookingStatusStyle(status: string): string {
  switch (status) {
    case 'confirmed':
      return 'bg-success-100 text-success-700';
    case 'pending_deposit':
    case 'pending_balance':
      return 'bg-warning-100 text-warning-700';
    case 'completed':
      return 'bg-primary-100 text-primary-700';
    case 'cancelled':
      return 'bg-warm-100 text-warm-600';
    default:
      return 'bg-warm-100 text-warm-600';
  }
}

// Icons
function ShowIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

function InquiryIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function RevenueIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );
}

function MapPinIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function InboxIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
    </svg>
  );
}

function UserPlusIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
    </svg>
  );
}

function BookingsIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
    </svg>
  );
}
